// The tool contract. A tool receives resolved parameters and returns an
// answer graph or NotFound. It never throws; failures travel in the Effect
// error channel. See Design > Toolsets and tools.

import { Schema, type Effect } from "effect";
import { ShapedAnswer, type ThingSnapshot } from "./answer.ts";
import type { ShapeDef } from "./defs.ts";
import type { WarmHubError } from "./errors.ts";
import { ParsedJsonObject } from "./json.ts";
import type { WarmHubReader } from "./warmhub-reader.ts";

/** A WarmHub repository, as "org/repo", such as "warmhub-data/us.geography". */
export type RepoId = string;

/**
 * Decodes a ThingRecord read back from where the platform stored it as JSON,
 * such as the lookup cache in KV. Its data, already JSON, isn't walked.
 */
export const ThingRecord = Schema.Struct({
  /** The repository the read named, which WarmHub's response doesn't say. */
  repo: Schema.String,
  /** The Thing's current name, which can change; `pinned` and `durableId` don't. */
  name: Schema.String,
  version: Schema.Number,
  /** The Thing's pinned canonical wref, such as "wh:warmhub-data/us.geography/place/identity/ansi/02404590@v2". */
  pinned: Schema.String,
  durableId: Schema.optionalKey(Schema.String),
  /** The shape's name, such as "Place". */
  shapeName: Schema.String,
  /** The pinned shape ref, such as "wh:warmhub-data/us.geography/Shape/Place@v2". */
  shapeRef: Schema.String,
  data: ParsedJsonObject,
});
/**
 * A Thing as the hub uses it: its current name, a pinned canonical wref, its
 * declared shape, and its data. Pass it to snapshot() to put it in an answer,
 * under its `pinned` wref.
 */
export type ThingRecord = typeof ThingRecord.Type;

/**
 * One reference that `refs` found: the Thing at the other end, whose wref is
 * `id`, its shape's name, and the field that holds the reference.
 */
export type RefItem = { id: string; shapeName?: string; fieldPath?: string };

// A number in a predicate is finite: JSON, and so WarmHub, has no NaN or
// Infinity, and the Curator API's OpenAPI document gives clients plain numbers.
const Scalar = Schema.Union([Schema.String, Schema.Finite, Schema.Boolean]);
/**
 * Decodes the `where` predicates of a query or count, as WarmHub takes them:
 * a comparison with a scalar, "in" with a list of scalars, or "exists" with
 * no value. A Thing matches when it matches every one.
 */
export const Where = Schema.Array(Schema.Union([
  Schema.Struct({ fieldPath: Schema.String, op: Schema.Literals(["eq", "ne", "gt", "gte", "lt", "lte", "prefix"]), rhs: Scalar }),
  Schema.Struct({ fieldPath: Schema.String, op: Schema.Literal("in"), rhs: Schema.mutable(Schema.Array(Scalar)) }),
  Schema.Struct({ fieldPath: Schema.String, op: Schema.Literal("exists") }),
]));
/** The `where` predicates of a query or count. A Thing matches when it matches every one. */
export type Where = typeof Where.Type;

// The options of WarmHubReads' and the WarmHubReader's query, count, refs and
// search. The WarmHubReader decodes them from a tool sandbox's RPC call, and
// their types are derived here, so the two can't drift. An optional option
// may be undefined, as structured clone keeps it from a bundle: it reads as
// absent. See Design > Code standards, "Types come from their schemas".
const Limit = Schema.optional(Schema.Int);
/** Decodes a query's options: one shape, the predicates every match meets, and at most how many. */
export const QueryOptions = Schema.Struct({ shape: Schema.String, where: Schema.optional(Where), limit: Limit });
/** A query's options. */
export type QueryOptions = typeof QueryOptions.Type;
/** Decodes a count's options: one shape, and the predicates every counted Thing meets. */
export const CountOptions = Schema.Struct({ shape: Schema.String, where: Schema.optional(Where) });
/** A count's options. */
export type CountOptions = typeof CountOptions.Type;
/** Decodes a refs call's options: the direction, and optionally a field path, a page size and the cursor of the next page. */
export const RefsOptions = Schema.Struct({
  direction: Schema.Literals(["inbound", "outbound"]), fieldPath: Schema.optional(Schema.String), limit: Limit, cursor: Schema.optional(Schema.String),
});
/** A refs call's options. */
export type RefsOptions = typeof RefsOptions.Type;
/** Decodes a search's options: one shape, and at most how many. */
export const SearchOptions = Schema.Struct({ shape: Schema.String, limit: Limit });
/** A search's options. */
export type SearchOptions = typeof SearchOptions.Type;

/**
 * WarmHub reads, each in a named repository: what a hub's dispatch reads
 * through its HubDispatch. A tool reads through the WarmHubReader, which has
 * the same query, count, refs and search.
 */
export type WarmHubReads = {
  /**
   * Every read names its repository. WarmHub resolves a durable ID in any
   * repository it can read, and its response doesn't name the repository, so
   * the record's repo is the one the read named.
   */
  get(ref: string, repo: RepoId): Effect.Effect<ThingRecord, WarmHubError>;
  /** `fresh` tags the call in the call log as a re-read, not a lookup cache miss. */
  getMany(refs: string[], repo: RepoId, opts?: { fresh?: true }): Effect.Effect<ThingRecord[], WarmHubError>;
  /**
   * The Things of one shape that match every `where` predicate: at most
   * `limit`, 50 by default, and never more than 500, WarmHub's largest page. Count first
   * when a tool needs every match.
   */
  query(repo: RepoId, opts: QueryOptions): Effect.Effect<ThingRecord[], WarmHubError>;
  /**
   * How many Things of one shape match every `where` predicate. Fails rather
   * than return a partial total when WarmHub's count stops at its window.
   */
  count(repo: RepoId, opts: CountOptions): Effect.Effect<number, WarmHubError>;
  /**
   * The references into (`inbound`) or out of (`outbound`) one Thing, a page
   * at a time: pass `nextCursor` back as `cursor` for the next page.
   */
  refs(repo: RepoId, ref: string, opts: RefsOptions): Effect.Effect<{ items: RefItem[]; nextCursor?: string }, WarmHubError>;
  /** A text search over one shape's Things, at most `limit` of them, 10 by default. */
  search(repo: RepoId, text: string, opts: SearchOptions): Effect.Effect<ThingRecord[], WarmHubError>;
  /** One version of a shape's definition. */
  shape(repo: RepoId, name: string, version: number): Effect.Effect<ShapeDef, WarmHubError>;
};

/** Decodes a Provenance entry, such as one in a tool outcome that crossed RPC. */
export const Provenance = Schema.Struct({ label: Schema.String, wref: Schema.String });
/**
 * Where an answer's facts came from: a label and a canonical wref. A tool
 * lists one for each source it read, beside its Answer.
 */
export type Provenance = typeof Provenance.Type;

/**
 * A parameter as resolved from the question: its raw text, and the Thing or
 * the choice it resolved to. An entity parameter is present only when it
 * resolved to one Thing, and then always has `thing`. A choice parameter
 * has `choice` when its value matched one of its choices, and otherwise only
 * `raw`. A text parameter has only `raw`.
 */
export type ResolvedParam = {
  name: string;
  /** The text the question gave for the parameter. */
  raw: string;
  /** Set when an entity value resolved to one Thing. */
  thing?: ThingRecord;
  /** Set when a choice value matched one of its choices: the choice's key, not the synonym asked. */
  choice?: string;
};

/**
 * What a tool receives: the question, and its resolved parameters by name.
 * An optional parameter is absent when the question didn't give it, when it's
 * a text parameter that doesn't match its pattern, or when it's an entity
 * parameter that resolved to no Thing or to more than one. A required one that's missing or
 * doesn't resolve never reaches the tool: the HubRouter answers
 * MissingParameters instead of calling it.
 */
export type ToolInput = { question: string; params: Record<string, ResolvedParam> };

/** Decodes a ToolResult, such as one in a tool outcome that crossed RPC from its tool sandbox. */
export const ToolResult = Schema.TaggedUnion({
  Answer: { answer: ShapedAnswer, provenance: Schema.mutable(Schema.Array(Provenance)), method: Schema.optionalKey(Schema.String) },
  NotFound: { detail: Schema.Trim.check(Schema.isNonEmpty()) },
});
/**
 * What a tool succeeds with: an Answer, with its provenance and optionally
 * the method that found it, or NotFound, with a detail the consumer reads.
 * Build an Answer's `answer` with AnswerBuilder, and NotFound with notFound().
 */
export type ToolResult = typeof ToolResult.Type;

/**
 * A tool's own failure, such as a fetch that failed. `retryable` says whether
 * the same question might succeed later. Fail with it (`Effect.fail(new
 * ToolError({ ... }))`) rather than throw: the tool sandbox reports it as a
 * ToolFailure that keeps `message` and `retryable`, while a thrown value
 * becomes a non-retryable ToolFailure. `message` reaches the consumer, as
 * the detail of a HubError at stage Tool, so write it for them, and keep
 * secrets and internals out of it. It can't be blank: the constructor throws
 * on "" or only whitespace, and a ToolFailure whose message is blank is a
 * HubError at stage ToolContract. The platform trims it.
 */
export class ToolError extends Schema.TaggedError<ToolError>()("ToolError", {
  message: Schema.String.check(Schema.isPattern(/\S/)),
  retryable: Schema.Boolean,
}) {}

/**
 * A tool: a function from the question and its resolved parameters to an
 * Effect of an Answer or NotFound. It never throws; it fails with a
 * WarmHubError or a ToolError. A tool reads WarmHub through the
 * WarmHubReader, and fetches through plain fetch(), which the EgressGateway
 * carries in a tool sandbox: HTTPS GET or HEAD to the hub's egress allowlist
 * only.
 *
 * A tool keeps no per-request state at module level: a warm tool sandbox
 * serves every consumer of the hub, call after call and calls at once, so a
 * counter, a cache, or a variable set before a read and used after it
 * carries one consumer's question into another's answer. Check it with
 * expectNoCarryOver from testing.ts. See Design > Toolsets and tools, "Tools
 * and the Tool SDK".
 */
export type Tool = (input: ToolInput) => Effect.Effect<ToolResult, WarmHubError | ToolError, WarmHubReader>;

/**
 * A Thing as an answer includes it: its shape ref, name, durable ID and data.
 * Pass the result to AnswerBuilder.thing() under the Thing's `pinned` wref,
 * since an answer's Things must be pinned (Design > Answer shapes, rule 7).
 */
export const snapshot = (t: ThingRecord): ThingSnapshot => ({
  shape: t.shapeRef,
  name: t.name,
  ...(t.durableId ? { durableId: t.durableId } : {}),
  data: t.data,
});

/**
 * A NotFound result: the question was in scope, but the hub holds no answer.
 * `detail` reaches the consumer, so say what was looked for. It's trimmed,
 * and can't be blank: notFound("") or notFound(" ") throws, and a NotFound
 * whose detail is blank is a HubError at stage ToolContract.
 */
export const notFound = (detail: string): ToolResult => ToolResult.cases.NotFound.make({ detail: detail.trim() });
